/**
 * Inline renderer for i18n strings that contain `{{term:slug}}` markers.
 *
 * Splits the translated string on glossary markers (see
 * `web/src/features/quiz/glossary.ts`) and replaces each marker with the
 * term's surface label wrapped in a dotted-underline button. The button
 * opens a small popover with the 1-sentence definition.
 *
 * - Desktop: hover or keyboard-focus reveals the popover.
 * - Mobile: tap toggles the popover (closed by tapping the trigger again
 *   or anywhere outside it).
 * - When `prefers-reduced-motion: reduce` is active, the popover skips
 *   transition (no fade).
 *
 * Unknown slugs fall back to literal `{{term:slug}}` so the string still
 * renders even if the i18n file references a missing glossary entry.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/components/ui/cn';
import { isLangCode, type LangCode } from '@/features/wiki/architecture';
import { parseGlossaryString, type GlossSegment } from '../glossary';

export interface GlossTextProps {
  /** i18n key resolved through useTranslation; markers are stripped from result. */
  i18nKey: string;
  /**
   * Forwarded to the wrapper. The wrapper is a span so the component is
   * free to live inside `<p>`, `<label>`, list items, etc.
   */
  className?: string;
}

export function GlossText({ i18nKey, className }: GlossTextProps) {
  const { t, i18n } = useTranslation();
  // i18n may be missing in test environments that mock `useTranslation` —
  // fall back to EN so render still works (tests assert on labels, not on
  // the localised tooltip content).
  const raw = t(i18nKey);
  const lang: LangCode =
    i18n && isLangCode(i18n.language) ? (i18n.language as LangCode) : 'en';
  const segments = parseGlossaryString(raw, lang);

  return (
    <span className={className}>
      {segments.map((seg, idx) =>
        seg.kind === 'text' ? (
          <span key={`t-${idx}`}>{seg.text}</span>
        ) : (
          <GlossTerm key={`g-${idx}-${seg.slug}`} segment={seg} />
        ),
      )}
    </span>
  );
}

function GlossTerm({ segment }: { segment: Extract<GlossSegment, { kind: 'term' }> }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const { label, short } = segment.entry[segment.lang];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        className={cn(
          'underline decoration-dotted decoration-ink-400 underline-offset-[3px]',
          'text-inherit hover:decoration-terra-600 focus-visible:outline focus-visible:outline-2',
          'focus-visible:outline-offset-2 focus-visible:outline-terra-600',
          'transition-colors',
        )}
      >
        {label}
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'absolute left-0 top-full z-30 mt-2 w-64 max-w-[18rem] rounded-md border border-ink-200',
            'bg-paper px-3 py-2 text-left font-sans text-[13px] leading-snug text-ink-700 shadow-lg',
            'motion-safe:animate-[rmFadeIn_120ms_ease-out]',
          )}
        >
          {short}
        </span>
      )}
    </span>
  );
}
